import {SearchAnalytics, SearchContext, SearchPhase, SearchTrigger} from './search-analytics';
import { HttpParams } from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject, ReplaySubject, Subject} from 'rxjs';
import {
  HTTPSearchParams, HTTPSearchRequest, HTTPSearchResponse,
  HTTPSearchResultFiltersResponse,
  HTTPSearchResultTourMap,
  PostFilterParams,
  SearchChunkState, SearchParamsFindType, StateFilters
} from "../../models";
import {HttpSearchService} from "./search-requests.service";
import {ToursStateService} from "../tours-state.service";
import {WindowRefService} from "../window-ref.service";
import {EventBusService} from "../event-bus.service";
import {environment} from "../../../environments/environment";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../config/hotel-tours-list-page.config";


@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private analytics: SearchAnalytics;
  private newSearchStartedSub = new BehaviorSubject<string>('');
  private onNewChunkSub = new Subject<SearchChunkState>();
  private searchProgressTimeoutSub = new Subject<number>();
  private postfilterProgressTimeoutSub = new Subject<number>();
  private searchIsDoneSub = new Subject<string>();
  private toursNotFoundSub = new BehaviorSubject<boolean>(false);
  private searchStartFailedSub = new BehaviorSubject<boolean>(false);
  private searchStartErrorMessageSub = new BehaviorSubject<string>('');
  private toursCountSub = new BehaviorSubject<number>(0);
  private pagesCountSub = new BehaviorSubject<number>(0);
  private firstToursFoundSub = new BehaviorSubject<boolean>(false);
  private lastSearchParams: HTTPSearchParams = null;
  private postFiltersSearchStartedSub = new Subject<PostFilterParams>();
  private filtersSub = new ReplaySubject<HTTPSearchResultFiltersResponse>(1);
  private postfilterSearchSub = new Subject<void>();
  private mapToursSub = new Subject<HTTPSearchResultTourMap[]>();
  private filtersStateSub = new Subject<StateFilters>();

  // Изменение выдачи по событиям
  private pageChangedSub = new Subject<void>();
  private sortChangedSub = new Subject<void>();
  private filtersApplyedSub = new Subject<void>();
  private filtersResetSub = new Subject<void>();

  // Событие для рекомендаций в выдаче
  private needSearchRecommendationsSub = new BehaviorSubject<boolean>(false);

  newSearchStarted$ = this.newSearchStartedSub.asObservable();
  onNewChunk$ = this.onNewChunkSub.asObservable();
  searchProgressTimeout$ = this.searchProgressTimeoutSub.asObservable();
  postfilterProgressTimeout$ = this.postfilterProgressTimeoutSub.asObservable();
  filtersState$ = this.filtersStateSub.asObservable();
  searchIsDone$ = this.searchIsDoneSub.asObservable();
  toursNotFound$ = this.toursNotFoundSub.asObservable();
  searchStartFailed$ = this.searchStartFailedSub.asObservable();
  searchStartErrorMessage$ = this.searchStartErrorMessageSub.asObservable();
  toursCount$ = this.toursCountSub.asObservable();
  pagesCount$ = this.pagesCountSub.asObservable();
  firstToursFound$ = this.firstToursFoundSub.asObservable();
  postFiltersSearchStarted$ = this.postFiltersSearchStartedSub.asObservable();
  filters$ = this.filtersSub.asObservable();

  postfilterSearch$ = this.postfilterSearchSub.asObservable();
  mapTours$ = this.mapToursSub.asObservable();

  // Изменение выдачи по событиям
  pageChanged$ = this.pageChangedSub.asObservable();
  sortChanged$ = this.sortChangedSub.asObservable();
  filtersApplyed$ = this.filtersApplyedSub.asObservable();
  filtersReset$ = this.filtersResetSub.asObservable();

  needSearchRecommendations$ = this.needSearchRecommendationsSub.asObservable();

  // Событие применение фильтров
  private applyFiltersSubject = new Subject<void>();
  applyFilters$ = this.applyFiltersSubject.asObservable();

  constructor(
    private httpSearch: HttpSearchService,
    private state: ToursStateService,
    private windowRef: WindowRefService,
    private eventBusService: EventBusService,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private appConfig: HotelToursPageConfig,
  ) {
    this.analytics = new SearchAnalytics(this.windowRef.nativeWindow, 'general', environment.production);
  }

  startNewSearch(formParams: any, onlyHotels: boolean, eventIndex?: number, trigger: SearchTrigger = 'initial') {
    const searchRequest = this.createSearchRequest(formParams, onlyHotels);

    this.analytics.begin(searchRequest.params, trigger);
    this.prepareSearchStartState();
    this.startSearchRequest(searchRequest, onlyHotels, eventIndex);
  }

  repeatLastSearch(eventIndex?: number): boolean {
    if (!this.lastSearchParams) {
      return false;
    }

    const searchRequest: HTTPSearchRequest = {
      type: SearchParamsFindType.DEFAULT,
      ukey: this.windowRef.nativeWindow.app.ukey,
      bkey: this.windowRef.nativeWindow.app.bkey,
      params: {...this.lastSearchParams},
    };

    this.analytics.begin(searchRequest.params, 'repeat');
    this.prepareSearchStartState();
    this.startSearchRequest(searchRequest, !!this.lastSearchParams.onlyHotels, eventIndex);
    return true;
  }

  private prepareSearchStartState(): void {
    // The results component can be recreated when leaving the empty state.
    this.newSearchStartedSub.next('');
    this.firstToursFoundSub.next(false);
    this.toursNotFoundSub.next(false);
    this.searchStartFailedSub.next(false);
    this.searchStartErrorMessageSub.next('');
    this.updateFilters(null);
    this.state.clearState();
    this.state.resetFilters();
    this.filtersReset(false);

    this.updateToursCount(0);
  }

  private startSearchRequest(searchRequest: HTTPSearchRequest, onlyHotels: boolean, eventIndex?: number): void {
    const context = this.analytics.context();
    this.httpSearch
      .startSearch(searchRequest, eventIndex)
      .subscribe({
        next: (response: HTTPSearchResponse) => {
          if (!this.isCurrentSearch(context)) { return; }
          if (response?.error || !response?.id) {
            this.handleStartSearchError(response?.error || 'Search response has no id');
            return;
          }

          this.newSearchStartedSub.next(response.id);
          this.searchProgressTimeoutSub.next(response.timeout);
          this.needSearchRecommendationsSub.next(this.isNeedSearchRecommendations(searchRequest.params.hotels));
        },
        error: (error) => {
          if (!this.isCurrentSearch(context)) { return; }
          this.handleStartSearchError(this.extractErrorMessage(error));
        }
      });

    this.eventBusService.isOnlyHotelsSearch$.next(onlyHotels);
  }

  /**
   * Возвращает таймаут поиска по типу
   * @param type
   */
  getSearchTimeout(type: 'main' | 'hotel'): number {
    return type === 'main' ? 30 : 10;
  }

  setFiltersState(state) {
    this.filtersStateSub.next(state);
  }

  setPostfilterTimeout(timeout: number): void {
    this.postfilterProgressTimeoutSub.next(timeout);
  }

  postfilterSearch(): void {
    this.analytics.markFilters();
    this.postfilterSearchSub.next();
  }

  onNewChunk(searchId: string, toursChunkCount: number, isDone: boolean) {
    this.onNewChunkSub.next({
      searchId,
      toursChunkCount,
      isDone,
    });
  }

  searchIsDone(searchId: string) {
    this.searchIsDoneSub.next(searchId);
  }

  filtersApplyed(): void {
    this.analytics.markFilters();
    this.filtersApplyedSub.next();
  }

  filtersReset(emitResultChange = true): void {
    if (emitResultChange) {
      this.analytics.markFilters();
      this.filtersResetSub.next();
    }
  }

  applyFilters() {
    this.applyFiltersSubject.next();
  }

  sortChanged(): void {
    this.sortChangedSub.next();
  }

  notFoundTours(isNotFound = true) {
    this.toursNotFoundSub.next(isNotFound);
  }

  updateToursCount(count: number): void {
    this.toursCountSub.next(count);
  }

  updatePageCount(count: number): void {
    this.pagesCountSub.next(count);
  }

  updateFilters(filters: HTTPSearchResultFiltersResponse): void {
    this.filtersSub.next(filters);
  }

  updateMapTours(tours: HTTPSearchResultTourMap[]): void {
    this.mapToursSub.next(tours);
  }

  pageChanged(): void {
    this.pageChangedSub.next();
  }

  firstToursFound(): void {
    if (this.firstToursFoundSub.value === false) {
      this.firstToursFoundSub.next(true);
    }
  }

  getLastSearchParams(): HTTPSearchParams | null {
    return this.lastSearchParams;
  }

  isNeedSearchRecommendations(hotels) {
    // if (hotels.length !== 0 && hotels.length < 4) {
    //   return false;
    // }

    return true;
  }

  private getDate(date) {
    let year = date.getFullYear(); // Получаем год
    let month = String(date.getMonth() + 1).padStart(2, '0'); // Получаем месяц и добавляем ведущий ноль
    let day = String(date.getDate()).padStart(2, '0'); // Получаем день и добавляем ведущий ноль

    return `${year}-${month}-${day}`; // Формируем строку в формате Y-m-d
  }

  private createSearchRequest(formParams: any, isOnlyHotels: boolean): HTTPSearchRequest {
    const dateFrom = this.getDate(new Date(formParams.dateFrom));
    const dateTo = this.getDate(new Date(formParams.dateTo));

    const params: HTTPSearchParams = {
      adults: Number(formParams.adults),
      childAges: formParams.childAges,
      countryId: formParams.country,
      dateFrom: dateFrom,
      dateTo: dateTo,
      ...(isOnlyHotels ? {} : {departCityId: formParams.departCity}),
      groupMode: 1,
      hotels: formParams.hotels,
      nightsFrom: formParams.nightsFrom,
      nightsTo: formParams.nightsTo,
      onlyHotels: isOnlyHotels,
      currency: (window as any).app.currency,
    };

    if (formParams.regionIds) {
      params.regions = formParams.regionIds;
    } else if (formParams.region) {
      params.regions = [formParams.region];
    }

    if (formParams.starsList && formParams.starsList !== 'any') {
      params.stars = formParams.starsList;
    } else if (formParams.stars && formParams.stars !== 'any') {
      params.stars = Array.isArray(formParams.stars) ? formParams.stars : [formParams.stars];
    }

    if (formParams.mealIds) {
      params.meals = formParams.mealIds;
    }
    if (formParams.operatorIds) {
      params.operators = formParams.operatorIds;
    }
    if (formParams.airlineCodes) {
      params.airlines = formParams.airlineCodes;
    }

    this.lastSearchParams = params;

    const request: HTTPSearchRequest = {
      type: SearchParamsFindType.DEFAULT,
      ukey: this.windowRef.nativeWindow.app.ukey,
      bkey: this.windowRef.nativeWindow.app.bkey,
      params,
    };

    return request;
  }

  private handleStartSearchError(message: string): void {
    console.error('[SearchService] startNewSearch failed:', message);
    this.trackSearchResult('search_result_error', this.analytics.context(), 'initial', {error_message: message});
    this.searchStartFailedSub.next(true);
    this.searchStartErrorMessageSub.next(message);
    this.notFoundTours(true);
    this.updateToursCount(0);
    this.updatePageCount(0);
    this.needSearchRecommendationsSub.next(false);
    this.eventBusService.hideToursLoader();
  }

  private extractErrorMessage(error: any): string {
    if (typeof error?.error === 'string' && error.error.trim() !== '') {
      return error.error;
    }
    if (typeof error?.error?.error === 'string' && error.error.error.trim() !== '') {
      return error.error.error;
    }
    if (typeof error?.error?.message === 'string' && error.error.message.trim() !== '') {
      return error.error.message;
    }
    if (typeof error?.message === 'string' && error.message.trim() !== '') {
      return error.message;
    }

    return 'Unknown search start error';
  }

  trackPageOpen(params: any): void { this.analytics.openPage(params || {}); }

  trackAlternativeDates(context: SearchContext, count: number): void {
    this.analytics.alternativeDates(context, count);
  }

  getAnalyticsContext(): SearchContext | null { return this.analytics.context(); }

  isCurrentSearch(context: SearchContext): boolean {
    return context?.search_attempt_id === this.analytics.context()?.search_attempt_id;
  }

  trackSearchResult(event: string, context = this.analytics.context(), phase?: SearchPhase, properties: Record<string, any> = {}): void {
    this.analytics.result(event, context, phase, properties);
  }

  private sendSearchEvent(eventName: string): void {
    this.analytics.result(eventName);
  }
}
